import './styles/base.css';
import './hud/styles/tokens.css';
import './hud/styles/hud.css';
import { createGame, STEP_HZ } from './game/game.ts';
import { attachLoader, nextPaint } from './hud/loader.ts';
import { createFlow, type Flow } from './flow/gameFlow.ts';
import { createGameAudio, type GameAudio } from './audio/gameAudio.ts';
import { loadSave } from './storage/storage.ts';

const canvas = document.querySelector<HTMLCanvasElement>('#stage');
if (!canvas) throw new Error('HEADWATER: #stage canvas missing from index.html');

const params = new URLSearchParams(location.search);
// v1.1 hook, dev builds only: ?mode=canyon flies the canyon with the chase camera.
const mode = import.meta.env.DEV && params.get('mode') === 'canyon' ? 'canyon' : 'classic';
// Dev builds only: ?paused leaves the loop stopped, so tests own time from the first frame (software WebGL on CI
// takes seconds per frame, and a running loop starves the test's own calls).
const paused = import.meta.env.DEV && params.has('paused');
// Dev builds only: ?level=n starts the title demo (and so the first run) at level n.
const startLevel =
  import.meta.env.DEV && params.has('level') ? Math.max(1, Number(params.get('level')) || 1) : undefined;
// Dev builds only: ?seed=n numbers the run seeds from n, so a test replays exactly (production draws them).
const runSeed = import.meta.env.DEV && params.has('seed') ? Math.max(0, Number(params.get('seed')) || 0) : undefined;

/** The HUD's fonts, loaded before the title shows so nothing reflows (judgment call 11). */
async function loadFonts(): Promise<void> {
  if (!document.fonts?.load) return;
  const faces = [
    '500 12px "Chakra Petch"',
    '600 13px "Chakra Petch"',
    '700 40px "Chakra Petch"',
    '400 15px "IBM Plex Sans"',
  ];
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 4000));
  await Promise.race([Promise.all(faces.map((f) => document.fonts.load(f))).then(() => undefined), timeout]);
}

window.__hwBoot = (async () => {
  const loader = attachLoader();
  loader.progress(0.08);
  await loadFonts();
  loader.progress(0.25);
  // Building the world is one long synchronous stage: let the bar paint first.
  await nextPaint();
  const game = await createGame({
    canvas,
    width: window.innerWidth,
    height: window.innerHeight,
    mode,
    startLevel,
    runSeed,
  });
  loader.progress(0.75);
  await nextPaint();
  // Shaders compile behind the loader (in parallel where the GPU driver can).
  const { renderer, scene, camera } = game.stage;
  if (renderer.extensions.has('KHR_parallel_shader_compile'))
    await renderer.compileAsync(scene, camera).catch(() => undefined);
  else renderer.compile(scene, camera);
  loader.progress(0.95);

  let flow: Flow | null = null;
  let audio: GameAudio | null = null;
  if (game.sim) {
    const settings = loadSave().settings;
    audio = createGameAudio({ game, settings });
    flow = createFlow({ game, settings, audio, width: window.innerWidth, height: window.innerHeight });
    flow.ready();
  }
  window.addEventListener('resize', () => {
    game.resize(window.innerWidth, window.innerHeight);
    flow?.resize(window.innerWidth, window.innerHeight);
  });
  if (import.meta.env.DEV) {
    const { installTestHooks } = await import('./debug/testHooks.ts');
    installTestHooks(game, STEP_HZ, flow, audio);
  }
  game.render(1);
  loader.done();
  if (!paused) game.start();
  return { webgl2: game.stage.renderer.getContext() instanceof WebGL2RenderingContext };
})();
